import React from 'react'
import { useAutoGET } from '@hooks/fetch'
import { optionalQuerySymbol } from '@hooks/use-state-in-url'
import { isMobile } from '@utils/mediaQueries'

import { ProductExplorerConfig, ProductSearchResults } from './ProductExplorerTypes'

// Must match the page sizes in ProductExplorerPreRenderedHtmlService's OSGi configuration, which
// resolves the size the server pre-renders with. If they disagree, the seed-validity check below
// fails and the client simply fetches.
const MOBILE_PAGE_SIZE = 8
const DESKTOP_PAGE_SIZE = 20
const ACTIVE_FILTERS_SEARCH_PARAM_NAME = 'activeFilters'
const PAGE_SEARCH_PARAM_NAME = 'page'
const TEXT_SEARCH_SEARCH_PARAM_NAME = 'query'
const CANONICAL_LINK_SELECTOR = 'link[rel="canonical"]'
const ROBOTS_META_SELECTOR = 'meta[name="robots"]'
const ROBOTS_NOINDEX = 'noindex'
const ROBOTS_NOINDEX_FOLLOW = 'noindex, follow'

// Head tags taken over by syncSeoTagsWithFilterAndSearchParameters, kept so the server-rendered state
// can be put back once the last filter is cleared and the search is emptied.
let removedCanonicalLink: HTMLLinkElement | null = null
let addedRobotsMeta: HTMLMetaElement | null = null
let overriddenRobotsMeta: { element: HTMLMetaElement; originalContent: string } | null = null

// The endpoint response and the server-seeded pre-render share one shape, which is also what the
// component consumes downstream.
type ProductExplorerAPIResult = ProductSearchResults

function useProductExplorerSearchEndpoint(
  url: string,
  queryParameters: Record<string, string[] | any>,
  initialData?: ProductExplorerAPIResult,
) {
  return useAutoGET<ProductExplorerAPIResult>(
    url,
    queryParameters,
    undefined,
    initialData !== undefined,
    undefined,
    initialData,
  )
}

/**
 * Whether the server-rendered result still matches the query this client would issue.
 *
 * The server omits blank params (ProductListingPageQueryParams#addQueryParamIfNotBlank) and useGET
 * skips falsy values, so an absent param and '' must compare equal.
 */
function queryKeyMatches(
  clientParameters: Record<string, string | string[] | undefined>,
  serverQueryKey: Record<string, string> | undefined,
  parameterNames: string[],
): boolean {
  if (!serverQueryKey) {
    return false
  }
  return parameterNames.every((name) => String(clientParameters[name] ?? '') === String(serverQueryKey[name] ?? ''))
}

function activeFiltersToQueryParametersString(newActiveFilters: Record<string, Set<string>>): string {
  return Object.entries(newActiveFilters)
    .map(([key, values]) => (values?.size > 0 ? `${key}:${[...values].join(',')}` : ''))
    .filter((activeFilter) => Boolean(activeFilter))
    .join(';')
}

function queryParameterToActiveFilters(filtersString: string | null): Record<string, Set<string>> {
  if (!filtersString) {
    return {}
  }

  return Object.fromEntries(
    filtersString
      .split(';')
      .map((filterString) => filterString.split(':')) // array of [key, value1,value2]
      .filter(([, value]) => Boolean(value))
      .map(([key, valuesString]) => [key, new Set(valuesString.split(','))]),
  )
}

function updateQueryParameter(newActiveFilters: Record<string, Set<string>>) {
  const parametersValue = activeFiltersToQueryParametersString(newActiveFilters)
  const currentPage = getCurrentPageFromQueryParameter()

  const parameters = new URLSearchParams(location.search)

  if (Object.keys(newActiveFilters).length > 0 && currentPage > 1) {
    parameters.set(PAGE_SEARCH_PARAM_NAME, currentPage.toString())
  } else {
    parameters.delete(PAGE_SEARCH_PARAM_NAME)
  }
  if (parametersValue.toString().length > 0) {
    parameters.set(ACTIVE_FILTERS_SEARCH_PARAM_NAME, parametersValue.toString())
  } else {
    parameters.delete(ACTIVE_FILTERS_SEARCH_PARAM_NAME)
  }

  const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${optionalQuerySymbol(
    parameters.toString(),
  )}`
  window.history.replaceState({ path: newUrl }, '', newUrl)
}

function getCurrentPageFromQueryParameter() {
  const query = new URLSearchParams(location.search)
  const page = query.get(PAGE_SEARCH_PARAM_NAME)
  if (page && page.length > 0) {
    try {
      return Number(page)
    } catch {
      return 1
    }
  }

  return 1
}

function setPageParameterSearchQuery(newPage: number) {
  const query = new URLSearchParams(location.search)
  if (newPage > 1) {
    query.set(PAGE_SEARCH_PARAM_NAME, newPage.toString())
  } else {
    query.delete(PAGE_SEARCH_PARAM_NAME)
  }

  const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${optionalQuerySymbol(
    query.toString(),
  )}`

  window.history.replaceState({ path: newUrl }, '', newUrl)
}

function setTextSearchParameterSearchQuery(newTextSearch: string) {
  const query = new URLSearchParams(location.search)
  if (newTextSearch.length > 0) {
    query.set(TEXT_SEARCH_SEARCH_PARAM_NAME, newTextSearch)
  } else {
    query.delete(TEXT_SEARCH_SEARCH_PARAM_NAME)
  }

  const newUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}${optionalQuerySymbol(
    query.toString(),
  )}`

  window.history.replaceState({ path: newUrl }, '', newUrl)
}

function hasFilterOrSearchParametersInUrl() {
  // Mirrors the CDN rule (set-noindex-when-filter-or-search-params-are-present in config/cdn.yaml),
  // which keys on the presence of the parameters rather than on them having a value. The pagination
  // param is deliberately not part of this: paginated PLPs stay indexable.
  const parameters = new URLSearchParams(location.search)

  return parameters.has(ACTIVE_FILTERS_SEARCH_PARAM_NAME) || parameters.has(TEXT_SEARCH_SEARCH_PARAM_NAME)
}

function applySeoTagsForFilteredView() {
  const canonicalLink = document.head.querySelector<HTMLLinkElement>(CANONICAL_LINK_SELECTOR)
  if (canonicalLink) {
    removedCanonicalLink = canonicalLink
    canonicalLink.remove()
  }

  const robotsMeta = document.head.querySelector<HTMLMetaElement>(ROBOTS_META_SELECTOR)
  if (!robotsMeta) {
    const newRobotsMeta = document.createElement('meta')
    newRobotsMeta.setAttribute('name', 'robots')
    newRobotsMeta.setAttribute('content', ROBOTS_NOINDEX_FOLLOW)
    document.head.append(newRobotsMeta)
    addedRobotsMeta = newRobotsMeta

    return
  }

  // The page component renders a robots meta of its own whenever the page has robots tags (see
  // page/head.html). An existing "noindex" is already at least as restrictive as ours, and
  // overwriting it would downgrade a "nofollow" to "follow", so it is left untouched.
  if (robotsMeta.getAttribute('content')?.toLowerCase().includes(ROBOTS_NOINDEX)) {
    return
  }

  overriddenRobotsMeta = { element: robotsMeta, originalContent: robotsMeta.getAttribute('content') ?? '' }
  robotsMeta.setAttribute('content', ROBOTS_NOINDEX_FOLLOW)
}

function restoreSeoTagsForUnfilteredView() {
  if (removedCanonicalLink) {
    document.head.append(removedCanonicalLink)
    removedCanonicalLink = null
  }

  if (addedRobotsMeta) {
    addedRobotsMeta.remove()
    addedRobotsMeta = null
  }

  if (overriddenRobotsMeta) {
    overriddenRobotsMeta.element.setAttribute('content', overriddenRobotsMeta.originalContent)
    overriddenRobotsMeta = null
  }
}

/**
 * Keeps the indexing directives in sync with the filter selection and the text search: a filtered or
 * searched product list is a near-duplicate of the unfiltered one, so it must not be indexed, while
 * its product links should still be followed. Reverts to the server-rendered state once the last
 * filter is cleared and the search is emptied.
 */
function syncSeoTagsWithFilterAndSearchParameters() {
  if (hasFilterOrSearchParametersInUrl()) {
    applySeoTagsForFilteredView()
  } else {
    restoreSeoTagsForUnfilteredView()
  }
}

function useActiveFiltersInUrl() {
  const urlParameters = new URLSearchParams(location.search)
  const filtersString = urlParameters.get(ACTIVE_FILTERS_SEARCH_PARAM_NAME)
  const currentlyActiveFilters = queryParameterToActiveFilters(filtersString)
  const [activeFilters, setActiveFilters] = React.useState(currentlyActiveFilters)

  const handleActiveFiltersChange: React.Dispatch<
    React.SetStateAction<{
      [k: string]: Set<string>
    }>
  > = React.useCallback(
    (newValue) => {
      if (typeof newValue === 'function') {
        setActiveFilters((old) => {
          const valueToApply = newValue(old)
          updateQueryParameter(valueToApply)

          return valueToApply
        })
      } else {
        updateQueryParameter(newValue)
        setActiveFilters(newValue)
      }
    },
    [setActiveFilters],
  )

  return [activeFilters, handleActiveFiltersChange] as const
}

function useTextSearchInUrl() {
  const urlParameters = new URLSearchParams(location.search)
  const textSearchInUrl = urlParameters.get(TEXT_SEARCH_SEARCH_PARAM_NAME) || ''

  const [textSearch, setTextSearch] = React.useState(textSearchInUrl)

  const handleTextSearchChange: React.Dispatch<React.SetStateAction<string>> = React.useCallback(
    (newValue) => {
      if (typeof newValue === 'function') {
        setTextSearch((old) => {
          const valueToApply = newValue(old)
          setTextSearchParameterSearchQuery(valueToApply)

          return valueToApply
        })
      } else {
        setTextSearchParameterSearchQuery(newValue)
        setTextSearch(newValue)
      }
    },
    [setTextSearch],
  )

  return [textSearch, handleTextSearchChange] as const
}

export function useProductExplorerLogic(config: ProductExplorerConfig) {
  const pageSize = isMobile() ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE
  const [textSearch, setTextSearch] = useTextSearchInUrl()
  const [activeFilters, setActiveFilters] = useActiveFiltersInUrl()

  const [currentPage, setCurrentPage] = React.useState(getCurrentPageFromQueryParameter())

  const activeFiltersAsParametersString = React.useMemo(
    () => activeFiltersToQueryParametersString(activeFilters),
    [activeFilters],
  )

  // Runs on load and after every filter or search change, as the query parameters are written to the
  // URL with history.replaceState and the server-rendered head is therefore never re-evaluated.
  React.useEffect(() => {
    syncSeoTagsWithFilterAndSearchParameters()
  }, [activeFiltersAsParametersString, textSearch])

  const queryParameters = React.useMemo(
    () => ({
      ...config.searchEndpoint.defaultParams,
      [config.searchEndpoint.pageSizeParamName]: pageSize.toString(),
      [config.searchEndpoint.offsetParamName]: ((currentPage - 1) * pageSize).toString(),
      [config.searchEndpoint.activeFiltersParamName]: activeFiltersAsParametersString,
      [config.searchEndpoint.textSearchParamName]: textSearch,
    }),
    [
      config.searchEndpoint.defaultParams,
      config.searchEndpoint.textSearchParamName,
      config.searchEndpoint.pageSizeParamName,
      config.searchEndpoint.offsetParamName,
      textSearch,
      pageSize,
      currentPage,
      activeFiltersAsParametersString,
    ],
  )

  // Seed from the server-rendered result when it matches the query we would issue anyway, so the
  // React takeover reproduces the pre-rendered grid instead of collapsing it. Any mismatch (a deep
  // link the server did not pre-render, or a page size the server resolved differently) falls back
  // to fetching.
  const canUseInitialData = React.useMemo(
    () =>
      queryKeyMatches(queryParameters, config.initialData?.queryKey, [
        config.searchEndpoint.pageSizeParamName,
        config.searchEndpoint.offsetParamName,
        config.searchEndpoint.activeFiltersParamName,
        config.searchEndpoint.textSearchParamName,
      ]),
    [queryParameters, config.initialData, config.searchEndpoint],
  )

  const [searchState] = useProductExplorerSearchEndpoint(
    config.searchEndpoint.url,
    queryParameters,
    canUseInitialData ? config.initialData?.result : undefined,
  )

  const updateCurrentPage = React.useCallback(
    (newPage: number) => {
      setPageParameterSearchQuery(newPage)
      setCurrentPage(newPage)
    },
    [setCurrentPage],
  )

  const handleOptionsChange = React.useCallback(
    (filterId: string, newValue: Set<string>) => {
      setActiveFilters((old) => {
        const hadPreviousSelection = (old[filterId]?.size ?? 0) > 0
        const hasNewSelection = newValue.size > 0
        if (hadPreviousSelection || hasNewSelection) {
          updateCurrentPage(1)
        }
        return {
          ...old,
          [filterId]: newValue,
        }
      })
    },
    [setActiveFilters, updateCurrentPage],
  )

  const handleFilterReset = React.useCallback(() => {
    setActiveFilters({})
    setTextSearch('')
    updateCurrentPage(1)
  }, [setActiveFilters, setTextSearch, updateCurrentPage])

  return {
    currentPage,
    pageSize,
    endpointState: searchState,
    activeFilters,
    textSearch,
    handleOptionsChange,
    handleFilterReset,
    handlePageChange: updateCurrentPage,
    setTextSearch: (value: React.SetStateAction<string>) => {
      setTextSearch(value)
      updateCurrentPage(1)
    },
  }
}
